import React, { ChangeEvent, useContext, useState } from "react";
import styled from "styled-components";
import { omit } from "lodash";
import { FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import { useToasts } from "react-toast-notifications";
import { Theme } from "@literati/public-ui-shared-react";

import { CheckoutContext } from "contexts/CheckoutProvider";

import { isAPIError } from "lib/api/base";
import { CheckoutSession } from "lib/api/checkout";
import {
  Subscription,
  saveAddress,
  validateAddress,
} from "lib/api/subscription";

import { submitFormik } from "utils/forms";
import getStateFromZip from "utils/getStateFromZip";

import StepLayout, { StepLayoutProps } from "components/Step/StepLayout";
import {
  ShippingKey as Key,
  ShippingSubmit,
  ShippingValues,
} from "components/Step/stepTypes";

import { ShippingFormContentInline } from "components/Shipping/ShippingForm";
import { Address, ShippingKey } from "components/Shipping/shippingTypes";

const FORM_ID = "step-shipping";

export const VALIDATION_SCHEMA = Yup.object().shape({
  name: Yup.string().required("Required."),
  street1: Yup.string().required("Required."),
  city: Yup.string().required("Required."),
  state: Yup.string().required("Required."),
  postalCode: Yup.string().required("Required."),
});

export const INITIAL_VALUES: ShippingValues = {
  [Key.KIND]: "shipping",
  [Key.CITY]: "",
  [Key.NAME]: "",
  [Key.POSTAL_CODE]: "",
  [Key.PREVENT_ROUTING]: false,
  [Key.STATE]: "",
  [Key.STREET1]: "",
  [Key.STREET2]: "",
} as const;

const StyledDiv = styled.div`
  padding: 2rem 0;

  /* Custom breakpoint unsupported by PUSR */
  @media (min-width: 375px) {
    input[name="state"],
    input[name="postalCode"] {
      max-width: 160px;
    }
  }
`;

export type StepLayoutPropsRelationship = Omit<
  StepLayoutProps<ShippingValues>,
  "asForm" | "children" | "formId"
>;

export interface StepShippingProps extends StepLayoutPropsRelationship {
  checkoutSession?: CheckoutSession | null;
  childName?: string;
  initialValues?: ShippingValues;
  onSubmit: ShippingSubmit;
  saveAddressToContext?: boolean;
  saveAddressToSubscription?: boolean;
  subscription?: Subscription | null;
}

export const StepShipping = ({
  checkoutSession,
  childName,
  initialValues: _initialValues,
  onSubmit,
  saveAddressToContext = false,
  saveAddressToSubscription = false,
  subscription,
  ...stepLayoutProps
}: StepShippingProps) => {
  const { addToast } = useToasts();

  const { saveShippingAddress } = useContext(CheckoutContext);

  let prefillAddress;
  if (_initialValues) {
    prefillAddress = omit(_initialValues, "kind");
  }

  const [submittedAddress, setSubmittedAddress] = useState<Address | undefined>(
    prefillAddress
  );
  const [suggestedAddress, setSuggestedAddress] = useState<
    Address | undefined
  >();

  const handleChange = (
    e: ChangeEvent<HTMLInputElement>,
    formikProps: Omit<FormikProps<ShippingValues>, "onSubmit">
  ) => {
    const { handleChange, setValues, values } = formikProps;
    const { name, value } = e.target;

    handleChange(e);

    if (name === ShippingKey.POSTAL_CODE) {
      const { st } = getStateFromZip(value);
      setValues({ ...values, postalCode: value, state: st || values.state });
    }
  };

  const handleEdit = () => {
    setSuggestedAddress(undefined);
  };

  const handleSuggestedConfirm = async () => {
    if (suggestedAddress) {
      if (saveAddressToContext) {
        const itemId = checkoutSession?.currentItemId;
        saveShippingAddress?.(
          {
            ...suggestedAddress,
            phoneNumber: "",
            country: "US",
          },
          itemId
        );
      }

      const subId = subscription?.id;
      if (saveAddressToSubscription && subId) {
        const address = { ...suggestedAddress, country: "US" };
        await saveAddress?.({ address, subId });
      }

      return onSubmit?.(
        {
          kind: "shipping",
          ...suggestedAddress,
          preventRouting: false,
        },
        {} as FormikHelpers<ShippingValues>
      );
    }
  };

  const handleSubmit: ShippingSubmit = async (values, formikHelpers) => {
    setSubmittedAddress({ ...values });
    let suggested: ShippingValues;
    let invalid = false;
    let errorCode: string | undefined;
    // we do not accept addresses with these error codes
    // if there is another error, we will accept the address, under the assumption that the service is down
    const invalidCodes = ["address_not_found", "address_not_eligible"];

    return await submitFormik({
      onSubmit: async () => {
        const address = { ...values, country: "US" };
        const res = await validateAddress(address);
        if (isAPIError(res)) {
          // @TODO: ErrorResponseSchema in kids-api doesn't match the actual response
          // when that is fixed, we can remove the ignore lines
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          errorCode = res?.error?.errors?.[0]?.code;

          // show toast if address is invalid without suggested address
          // in this case, the API shape returns an array of errors
          if (errorCode && invalidCodes.includes(errorCode)) {
            invalid = true;
            console.error(res.error?.message || "Failed to validate address.");
            return;
          }
        }

        // if invalid with suggested address, set it to prompt the user to select the suggested address
        if (res?.isValid === false && res?.suggestedAddress) {
          suggested = res.suggestedAddress;
        } else if (res?.isValid === false) {
          invalid = true;
        } else {
          if (saveAddressToContext) {
            const itemId = checkoutSession?.currentItemId;
            saveShippingAddress?.({ ...address, phoneNumber: "" }, itemId);
          }

          const subId = subscription?.id;
          if (saveAddressToSubscription && subId) {
            return await saveAddress?.({ address, subId });
          }
        }
      },
      onSuccess: () => {
        if (invalid) {
          const toastMessage =
            errorCode === "address_not_eligible"
              ? "Addresses in Ann Arbor, MI are not eligible for subscriptions."
              : "This address is not valid.";
          addToast(toastMessage, {
            appearance: "error",
          });
          return;
        }
        if (suggested) {
          setSuggestedAddress({ ...suggested });
          onSubmit?.({ ...values, preventRouting: true }, formikHelpers);
          return;
        }
        setSubmittedAddress(undefined);
        setSuggestedAddress(undefined);

        return onSubmit?.(values, formikHelpers);
      },
      formikProps: { ...formikHelpers },
    });
  };

  return (
    <Theme>
      <StepLayout<ShippingValues>
        asForm
        bgColor="white"
        formId={FORM_ID}
        formikProps={{
          enableReinitialize: true,
          initialValues: {
            ...INITIAL_VALUES,
            ..._initialValues,
            ...submittedAddress,
            name: childName || submittedAddress?.name || "",
          },
          onSubmit: handleSubmit,
          validationSchema: VALIDATION_SCHEMA,
        }}
        suppressFooter={Boolean(suggestedAddress)}
        {...stepLayoutProps}
      >
        {(props) => (
          <StyledDiv>
            <ShippingFormContentInline
              onConfirm={handleSuggestedConfirm}
              onChange={(e) => handleChange(e, props)}
              onEdit={handleEdit}
              submittedAddress={submittedAddress}
              suggestedAddress={suggestedAddress}
            />
          </StyledDiv>
        )}
      </StepLayout>
    </Theme>
  );
};

export default StepShipping;
